"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  lendPhoneNumberAction,
  returnPhoneNumberLoanAction,
  takeOverPhoneNumberLoanAction,
} from "@/server-actions/phone-number-loans"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import { pbxPhoneNumbersQueryKey } from "@/hooks/queries/pbx-phone-numbers"
import { outgoingVoiceCallPhoneNumbersQueryKey } from "@/hooks/queries/outgoing-voice-calls"
import { campaignsQueryKey } from "@/hooks/queries/campaigns"
import { useTranslations } from "next-intl"


export function useLendPhoneNumberMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPhoneNumberLoans")

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { phoneNumber: string; destinationTenantId: string; sourceTenantId?: string }) => {
      const result = await withQueryHandling(lendPhoneNumberAction(input), {
        loading: tUi("lendingDemoNumber"),
        success: tUi("demoNumberLent"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: () => invalidateLoanQueries(queryClient),
  })
}

export function useReturnPhoneNumberLoanMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPhoneNumberLoans")

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { loanId: string }) => {
      return withQueryHandling(returnPhoneNumberLoanAction(input), {
        loading: tUi("returningDemoNumber"),
        success: tUi("demoNumberReturned"),
      })
    },
    onSuccess: () => invalidateLoanQueries(queryClient),
  })
}

export function useTakeOverPhoneNumberLoanMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsPhoneNumberLoans")

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      loanId: string
      phoneNumber: string
      sourceTenantId: string
      destinationTenantId: string
    }) => {
      const result = await withQueryHandling(takeOverPhoneNumberLoanAction(input), {
        loading: tUi("takingAwayAndLendingDemoNumber"),
        success: tUi("demoNumberLentHere"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: () => invalidateLoanQueries(queryClient),
  })
}

async function invalidateLoanQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
    queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
    queryClient.invalidateQueries({ queryKey: campaignsQueryKey }),
  ])
}
